import React from "react";
import { Nav, Card } from "ui-kit-ck-consultant";
import { Row, Col } from "react-flexbox-grid";
import FadeIn from "react-fade-in";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";

import HomeStats from "../../components/home/HomeStats";

import journauxImage from "../../images/home-journaux.svg";
import linkedinImage from "../../images/home-linkedin.svg";
import websiteImage from "../../images/home-website.svg";

import "./home.css";

// No `right` flag on /home in the registry, so this page is the one dashboard every
// company sees. Keep it that way: anything company-specific belongs in Statistiques.
const TILES = [
  {
    key: "journaux",
    title: "Les journaux LANG",
    text: "Retrouvez les éditions du journal du Groupe LANG & Associés : actualités du réseau, dossiers et temps forts.",
    cta: "Consulter les journaux",
    image: journauxImage,
    env: "REACT_APP_LANG_JOURNALS_URL",
  },
  {
    key: "linkedin",
    title: "LinkedIn",
    text: "Suivez la page du Groupe LANG & Associés pour ne rien manquer de ses derniers posts.",
    cta: "Suivre la page LinkedIn",
    image: linkedinImage,
    env: "REACT_APP_LANG_LINKEDIN_URL",
  },
  {
    key: "website",
    title: "Le site du Groupe LANG",
    text: "Découvrez le groupe, ses services, son pôle innovation et ses agences partout en France.",
    cta: "Visiter le site",
    image: websiteImage,
    env: "REACT_APP_LANG_WEBSITE_URL",
  },
];

export const buildTiles = (env) =>
  TILES.map((tile) => ({ ...tile, url: env[tile.env] || null }));

// A tile with no URL must not become a dead link, so production drops it. But dropping
// it *silently* is exactly how this page once rendered as a bare Nav: CRA reads .env
// only at startup, the dev server predated the variables, every URL came back undefined
// and all three tiles disappeared with nothing on screen to say why. So outside
// production the tile stays visible, disabled, naming the variable to fill in.
export const visibleTiles = (env) => {
  const tiles = buildTiles(env);
  return env.NODE_ENV === "production"
    ? tiles.filter((tile) => tile.url)
    : tiles;
};

const TileCard = ({ tile }) => (
  <Card className="home-tile-card" classNameChildren="home-tile-body">
    <img className="home-tile-media" src={tile.image} alt="" />
    <div className="home-tile-content">
      <h2 className="home-tile-title">{tile.title}</h2>
      <p className="home-tile-text">{tile.text}</p>
      {tile.url ? (
        <span className="home-tile-cta">
          {tile.cta}
          <FontAwesomeIcon icon={faArrowRight} />
        </span>
      ) : (
        <span className="home-tile-cta home-tile-cta-unset">
          <FontAwesomeIcon icon={faExclamationCircle} />
          Lien non configuré — {tile.env}
        </span>
      )}
    </div>
  </Card>
);

export default class Home extends React.Component {
  // `env` is injected by the tests; the router renders <Home/> with no props.
  getEnv = () => this.props.env || process.env;

  componentDidMount() {
    const env = this.getEnv();
    const missing = buildTiles(env).filter((tile) => !tile.url);

    // Production says it on screen (disabled tile / backstop card) rather than in a
    // console the user will never open, so only warn where a developer can act.
    if (missing.length && env.NODE_ENV !== "production") {
      console.error(
        `[home] URL manquante pour ${missing.length} tuile(s) : ${missing
          .map((tile) => tile.env)
          .join(
            ", ",
          )}. Renseignez-la dans client/.env — puis redémarrez : CRA ne lit .env qu'au démarrage, un rechargement de page ne suffit pas.`,
      );
    }
  }

  render() {
    const tiles = visibleTiles(this.getEnv());

    return (
      <>
        <Nav title="Tableau de bord" />
        <FadeIn>
          <Row>
            {tiles.map((tile) => (
              <Col key={tile.key} xs={12} md={4} className="home-col">
                {tile.url ? (
                  <a
                    className="home-tile"
                    href={tile.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <TileCard tile={tile} />
                  </a>
                ) : (
                  <div className="home-tile home-tile-disabled">
                    <TileCard tile={tile} />
                  </div>
                )}
              </Col>
            ))}
            {/* Backstop: a misconfigured build must still say something. */}
            {!tiles.length && (
              <Col xs={12}>
                <Card>Le tableau de bord n'est pas encore configuré.</Card>
              </Col>
            )}
          </Row>
        </FadeIn>
        {/* Company-scoped KPIs, below the LANG tiles. Self-guards on the auth context,
            so it renders nothing (and fetches nothing) when the page is mounted without
            a logged-in user — e.g. the Home unit tests. */}
        <HomeStats />
      </>
    );
  }
}
